import { CALENDAR_CURSOR_FIELDS, type Calendar } from "@app/core";
import type { StorageAdapter } from "../adapter/adapter.js";
import { ackedColumn, clampAck, type SyncDestination } from "./mut.js";

/** Built FROM `CALENDAR_CURSOR_FIELDS` so "which columns are the device-local
 *  cursor" has exactly one definition — core's list — instead of a comment here
 *  that can drift out of step with the statement below it. */
const CURSOR_SET = CALENDAR_CURSOR_FIELDS.map((c) => `${c}=?`).join(",");

const CALENDAR_CONTENT_SET = `name=?,color=?,hidden=?,owner_id=?,external_calendar_id=?,external_source=?,external_account=?,sync_direction=?,updated_at=?,deleted_at=?`;
const CALENDAR_UPDATE_SQL = `UPDATE calendars SET ${CALENDAR_CONTENT_SET},mut=mut+1 WHERE id=?`;
const CALENDAR_UPDATE_SYNCED_SQL: Record<SyncDestination, string> = {
  server: `UPDATE calendars SET ${CALENDAR_CONTENT_SET},mut=mut+1,server_acked_mut=mut+1 WHERE id=?`,
  google: `UPDATE calendars SET ${CALENDAR_CONTENT_SET},mut=mut+1,google_acked_mut=mut+1 WHERE id=?`,
};

/** CRUD over the `calendars` table. Returns plain Calendar objects.
 *
 *  NOTE: {@link update} no longer writes the device-local cursor columns
 *  (`sync_token`/`last_synced_at`) — {@link setSyncCursor} is their only writer.
 *  The old whole-row update let a pass-start copy revert a cursor the reconciler
 *  had advanced meanwhile; splitting the writers removes the read-modify-write. */
export class CalendarsRepository {
  constructor(private readonly db: StorageAdapter) {}

  /** `synced` marks the row as already in step with that destination (an inbound
   *  sync apply); a plain insert starts dirty for both. */
  async insert(c: Calendar, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      `INSERT INTO calendars (id,name,color,hidden,owner_id,external_calendar_id,external_source,
        external_account,sync_direction,sync_token,last_synced_at,mut,server_acked_mut,google_acked_mut,created_at,updated_at,deleted_at)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
      [c.id, c.name, c.color ?? null, c.hidden ? 1 : 0, c.owner_id ?? null,
       c.external_calendar_id ?? null, c.external_source ?? null, c.external_account ?? null,
       syncDirection(c), c.sync_token ?? null, c.last_synced_at ?? null,
       1, synced === "server" ? 1 : 0, synced === "google" ? 1 : 0,
       c.created_at, c.updated_at, c.deleted_at],
    );
  }

  /** Every content write bumps `mut` (the sync engine's per-row dirty fact);
   *  `synced` self-acknowledges a write whose content came FROM that destination. */
  async update(c: Calendar, synced?: SyncDestination): Promise<void> {
    await this.db.execute(
      synced ? CALENDAR_UPDATE_SYNCED_SQL[synced] : CALENDAR_UPDATE_SQL,
      [c.name, c.color ?? null, c.hidden ? 1 : 0, c.owner_id ?? null,
       c.external_calendar_id ?? null, c.external_source ?? null, c.external_account ?? null,
       syncDirection(c), c.updated_at, c.deleted_at, c.id],
    );
  }

  /**
   * Write ONLY the device-local provider cursor columns (`CALENDAR_CURSOR_FIELDS`)
   * — the sanctioned way for the Google reconciler to persist its `sync_token` /
   * poll watermark. Touches no content column, never `updated_at`, and never
   * `mut`: a poll must not dirty the calendar for either sync engine.
   */
  async setSyncCursor(
    id: string,
    cursor: Pick<Calendar, (typeof CALENDAR_CURSOR_FIELDS)[number]>,
  ): Promise<void> {
    await this.db.execute(
      `UPDATE calendars SET ${CURSOR_SET} WHERE id=?`,
      [...CALENDAR_CURSOR_FIELDS.map((c) => cursor[c] ?? null), id],
    );
  }

  /** Clamped acknowledgement — see `TasksRepository.ackMut`. */
  async ackMut(id: string, dest: SyncDestination, upTo: number): Promise<void> {
    const current = await this.findById(id);
    if (!current) return;
    const next = clampAck(current, dest, upTo);
    if (next === null) return;
    await this.db.execute(`UPDATE calendars SET ${ackedColumn(dest)}=? WHERE id=?`, [next, id]);
  }

  async listActive(): Promise<Calendar[]> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM calendars WHERE deleted_at IS NULL`,
    );
    return rows.map(fromRow);
  }

  /** Every row INCLUDING soft-deleted tombstones — for sync. */
  async listAll(): Promise<Calendar[]> {
    const rows = await this.db.select<Record<string, unknown>>(`SELECT * FROM calendars`);
    return rows.map(fromRow);
  }

  async findById(id: string): Promise<Calendar | null> {
    const rows = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM calendars WHERE id=?`, [id],
    );
    return rows[0] ? fromRow(rows[0]) : null;
  }

  async hardDelete(id: string): Promise<void> {
    await this.db.execute(`DELETE FROM calendars WHERE id=?`, [id]);
  }
}

function syncDirection(c: Calendar): Calendar["sync_direction"] {
  return c.sync_direction ?? "off";
}

function fromRow(r: Record<string, unknown>): Calendar {
  return {
    id: String(r["id"]),
    name: String(r["name"] ?? ""),
    color: (r["color"] as string | null) ?? null,
    hidden: Number(r["hidden"] ?? 0) === 1,
    owner_id: (r["owner_id"] as string | null) ?? null,
    external_calendar_id: (r["external_calendar_id"] as string | null) ?? null,
    external_source: (r["external_source"] as string | null) ?? null,
    external_account: (r["external_account"] as string | null) ?? null,
    sync_direction: (r["sync_direction"] as Calendar["sync_direction"]) ?? "off",
    sync_token: (r["sync_token"] as string | null) ?? null,
    last_synced_at: (r["last_synced_at"] as string | null) ?? null,
    mut: Number(r["mut"] ?? 0),
    server_acked_mut: Number(r["server_acked_mut"] ?? 0),
    google_acked_mut: Number(r["google_acked_mut"] ?? 0),
    created_at: String(r["created_at"]),
    updated_at: String(r["updated_at"]),
    deleted_at: (r["deleted_at"] as string | null) ?? null,
  };
}
